import { useState } from 'react';
import { Monitor, Send, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { Spinner } from '../../components/ui/Spinner';
import type { PreviewEmail } from '../../queries/brandEmailTheme';

const WIDTHS = { desktop: 600, phone: 390 } as const;
type Width = keyof typeof WIDTHS;

interface EmailGalleryProps {
  emails: PreviewEmail[] | undefined;
  loading: boolean;
  /** Why the preview could not be built, e.g. a logo size out of range. */
  error: string | null;
  canWrite: boolean;
  sending: boolean;
  sentTo: string | null;
  onSendTest: (key: string) => void;
}

/** Every email the brand sends, built from the design being edited. */
export function EmailGallery({ emails, loading, error, canWrite, sending, sentTo, onSendTest }: EmailGalleryProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [width, setWidth] = useState<Width>('desktop');

  if (error) return <Callout variant="error" title="The preview could not be built">{error}</Callout>;
  if (!emails) return loading ? <Spinner /> : null;
  if (emails.length === 0) return <p className="text-sm text-muted-foreground">This brand sends no emails yet.</p>;

  const selected = emails.find((e) => e.key === selectedKey) ?? emails[0];

  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Emails" className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border border-border p-1">
        {emails.map((email) => (
          <button
            key={email.key}
            type="button"
            role="tab"
            aria-selected={email.key === selected.key}
            onClick={() => setSelectedKey(email.key)}
            className={`rounded px-2 py-1.5 text-left text-sm ${email.key === selected.key ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
          >
            <span className="font-medium">{email.name}</span>
            <span className="block text-xs text-muted-foreground">{email.trigger}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Preview width" className="flex gap-1">
          <Button size="sm" variant={width === 'desktop' ? 'secondary' : 'ghost'} aria-pressed={width === 'desktop'} onClick={() => setWidth('desktop')}>
            <Monitor /> Desktop
          </Button>
          <Button size="sm" variant={width === 'phone' ? 'secondary' : 'ghost'} aria-pressed={width === 'phone'} onClick={() => setWidth('phone')}>
            <Smartphone /> Phone
          </Button>
        </div>
        {loading && <span className="text-xs text-muted-foreground">Updating…</span>}
        {canWrite && (
          <Button size="sm" variant="outline" className="ml-auto" disabled={sending} onClick={() => onSendTest(selected.key)}>
            <Send /> {sending ? 'Sending…' : 'Send me a test'}
          </Button>
        )}
      </div>
      {sentTo && <p className="text-xs text-muted-foreground">Test sent to {sentTo}.</p>}

      <dl className="space-y-1 text-sm">
        <div>
          <dt className="inline text-muted-foreground">Subject: </dt>
          <dd className="inline font-medium">{selected.subject}</dd>
        </div>
        <div>
          <dt className="inline text-muted-foreground">Preheader: </dt>
          <dd className="inline">{selected.preheader}</dd>
        </div>
      </dl>

      <div className="overflow-x-auto rounded-md border border-border bg-muted/40 p-2">
        <iframe
          title={`Preview of ${selected.name}`}
          srcDoc={selected.html}
          sandbox=""
          width={WIDTHS[width]}
          height={900}
          className="mx-auto block border-0 bg-white"
        />
      </div>
    </div>
  );
}
